/**
 * HoverThumbCache - IndexedDB-based persistent cache for hover thumbnails
 *
 * Features:
 * - Persistent storage (survives page refresh)
 * - Stores 1 thumb every N frames (configurable)
 * - LRU eviction when cache exceeds max size
 * - Blob URL management for React components
 */

const DB_NAME = 'timelapse-hover-thumbs';
const DB_VERSION = 1;
const STORE_NAME = 'thumbs';
const MAX_ENTRIES = 1000; // Max thumbs per view
const METADATA_KEY_PREFIX = 'hover-thumb-meta:';

interface ThumbEntry {
  key: string; // viewId:frameIndex
  viewId: string;
  frameIndex: number;
  blob: Blob;
  timestamp: number;
}

class HoverThumbCacheService {
  private db: IDBDatabase | null = null;
  private blobUrls: Map<string, string> = new Map(); // key -> blob URL
  private initPromise: Promise<void> | null = null;

  /**
   * Initialize IndexedDB
   */
  async init(): Promise<void> {
    if (this.db) return;
    if (this.initPromise) return this.initPromise;

    this.initPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error('[HoverThumbCache] Failed to open IndexedDB:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, { keyPath: 'key' });
          store.createIndex('viewId', 'viewId', { unique: false });
          store.createIndex('timestamp', 'timestamp', { unique: false });
        }
      };
    });

    return this.initPromise;
  }

  /**
   * Generate cache key from viewId and frame index
   */
  private makeKey(viewId: string, frameIndex: number): string {
    return `${viewId}:${frameIndex}`;
  }

  /**
   * Check if a thumb is cached
   */
  async has(viewId: string, frameIndex: number): Promise<boolean> {
    await this.init();
    if (!this.db) return false;

    const key = this.makeKey(viewId, frameIndex);

    return new Promise((resolve) => {
      const tx = this.db!.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const request = store.count(IDBKeyRange.only(key));

      request.onsuccess = () => resolve(request.result > 0);
      request.onerror = () => resolve(false);
    });
  }

  /**
   * Get a thumb blob URL (creates if needed)
   */
  async get(viewId: string, frameIndex: number): Promise<string | null> {
    await this.init();
    if (!this.db) return null;

    const key = this.makeKey(viewId, frameIndex);

    // Check in-memory cache first
    if (this.blobUrls.has(key)) {
      return this.blobUrls.get(key)!;
    }

    return new Promise((resolve) => {
      const tx = this.db!.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const request = store.get(key);

      request.onsuccess = () => {
        const entry = request.result as ThumbEntry | undefined;
        if (entry) {
          // Create blob URL and cache it
          const url = URL.createObjectURL(entry.blob);
          this.blobUrls.set(key, url);
          resolve(url);
        } else {
          resolve(null);
        }
      };

      request.onerror = () => resolve(null);
    });
  }

  /**
   * Store a thumb from a URL
   */
  async store(viewId: string, frameIndex: number, imageUrl: string): Promise<boolean> {
    await this.init();
    if (!this.db) return false;

    const key = this.makeKey(viewId, frameIndex);

    // Skip if already cached
    if (await this.has(viewId, frameIndex)) {
      return true;
    }

    try {
      // Fetch image as blob
      // Use unique cache-busting param to avoid CDN serving cached responses
      // without CORS headers (from <img> tags that load without Origin header)
      const separator = imageUrl.includes('?') ? '&' : '?';
      const response = await fetch(`${imageUrl}${separator}_cb=${Date.now()}`);
      if (!response.ok) return false;

      const blob = await response.blob();

      // Store in IndexedDB
      return new Promise((resolve) => {
        const tx = this.db!.transaction(STORE_NAME, 'readwrite');
        const store = tx.objectStore(STORE_NAME);

        const entry: ThumbEntry = {
          key,
          viewId,
          frameIndex,
          blob,
          timestamp: Date.now(),
        };

        const request = store.put(entry);

        request.onsuccess = () => {
          // Create blob URL
          const url = URL.createObjectURL(blob);
          this.blobUrls.set(key, url);
          resolve(true);
        };

        request.onerror = () => resolve(false);
      });
    } catch (error) {
      console.error('[HoverThumbCache] Failed to store thumb:', error);
      return false;
    }
  }

  /**
   * Get all cached frame indices for a view
   */
  async getCachedIndices(viewId: string): Promise<number[]> {
    await this.init();
    if (!this.db) return [];

    return new Promise((resolve) => {
      const tx = this.db!.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const index = store.index('viewId');
      const request = index.getAll(IDBKeyRange.only(viewId));

      request.onsuccess = () => {
        const entries = request.result as ThumbEntry[];
        resolve(entries.map((e) => e.frameIndex).sort((a, b) => a - b));
      };

      request.onerror = () => resolve([]);
    });
  }

  /**
   * Clear all thumbs for a view
   */
  async clearView(viewId: string): Promise<void> {
    await this.init();
    if (!this.db) return;

    // Revoke blob URLs for this view
    for (const [key, url] of this.blobUrls.entries()) {
      if (key.startsWith(`${viewId}:`)) {
        URL.revokeObjectURL(url);
        this.blobUrls.delete(key);
      }
    }

    return new Promise((resolve) => {
      const tx = this.db!.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const index = store.index('viewId');
      const request = index.openCursor(IDBKeyRange.only(viewId));

      request.onsuccess = () => {
        const cursor = request.result;
        if (cursor) {
          cursor.delete();
          cursor.continue();
        } else {
          resolve();
        }
      };

      request.onerror = () => resolve();
    });
  }

  /**
   * Get count of cached thumbs for a view
   */
  async getCount(viewId: string): Promise<number> {
    await this.init();
    if (!this.db) return 0;

    return new Promise((resolve) => {
      const tx = this.db!.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const index = store.index('viewId');
      const request = index.count(IDBKeyRange.only(viewId));

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => resolve(0);
    });
  }

  /**
   * Cleanup old entries if exceeding max
   */
  async cleanup(): Promise<void> {
    await this.init();
    if (!this.db) return;

    return new Promise((resolve) => {
      const tx = this.db!.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const countRequest = store.count();

      countRequest.onsuccess = () => {
        const count = countRequest.result;
        if (count <= MAX_ENTRIES) {
          resolve();
          return;
        }

        // Delete oldest entries
        const deleteCount = count - MAX_ENTRIES;
        const index = store.index('timestamp');
        const cursorRequest = index.openCursor();
        let deleted = 0;

        cursorRequest.onsuccess = () => {
          const cursor = cursorRequest.result;
          if (cursor && deleted < deleteCount) {
            const key = (cursor.value as ThumbEntry).key;
            // Revoke blob URL if exists
            if (this.blobUrls.has(key)) {
              URL.revokeObjectURL(this.blobUrls.get(key)!);
              this.blobUrls.delete(key);
            }
            cursor.delete();
            deleted++;
            cursor.continue();
          } else {
            resolve();
          }
        };

        cursorRequest.onerror = () => resolve();
      };

      countRequest.onerror = () => resolve();
    });
  }

  /**
   * Revoke all blob URLs (call on unmount)
   */
  revokeAllBlobUrls(): void {
    for (const url of this.blobUrls.values()) {
      URL.revokeObjectURL(url);
    }
    this.blobUrls.clear();
  }

  /**
   * Store metadata for a view (photos count)
   * Used to detect when cache is stale due to new photos
   */
  setViewMetadata(viewId: string, photosCount: number): void {
    try {
      localStorage.setItem(`${METADATA_KEY_PREFIX}${viewId}`, JSON.stringify({ photosCount }));
    } catch {
      // localStorage might be full or disabled
    }
  }

  /**
   * Get stored metadata for a view
   */
  getViewMetadata(viewId: string): { photosCount: number } | null {
    try {
      const data = localStorage.getItem(`${METADATA_KEY_PREFIX}${viewId}`);
      return data ? JSON.parse(data) : null;
    } catch {
      return null;
    }
  }

  /**
   * Check if cache is stale (photos count changed)
   * Returns true if cache should be cleared
   */
  isCacheStale(viewId: string, currentPhotosCount: number): boolean {
    const meta = this.getViewMetadata(viewId);
    if (!meta) return false; // No cached metadata, nothing to invalidate
    return meta.photosCount !== currentPhotosCount;
  }
}

// Singleton instance
export const hoverThumbCache = new HoverThumbCacheService();
